/// <reference types="google-publisher-tag" />

import { type StickyBannerDismissPageType, type StickyBannerPageType } from '@/types/ad-telemetry'

const AD_SIZE_970x90 = [970, 90] as googletag.SingleSizeArray
const AD_SIZE_970x250 = [970, 250] as googletag.SingleSizeArray
const AD_SIZE_728x90 = [728, 90] as googletag.SingleSizeArray
const AD_SIZE_336x280 = [336, 280] as googletag.SingleSizeArray
const AD_SIZE_300x250 = [300, 250] as googletag.SingleSizeArray
const AD_SIZE_320x50 = [320, 50] as googletag.SingleSizeArray
const AD_SIZE_320x100 = [320, 100] as googletag.SingleSizeArray
const AD_SIZE_1x1 = [1, 1] as googletag.SingleSizeArray

/** Size for the full3d mobile-portrait bottom banner ad. */
export const FULL3D_MOBILE_BANNER_AD_SIZE: googletag.MultiSize = [AD_SIZE_320x50]

const MULTISIZE_MB_AD_SIZES: googletag.MultiSize = [AD_SIZE_1x1, AD_SIZE_320x100, AD_SIZE_320x50]

const MULTISIZE_PC_AD_SIZES: googletag.MultiSize = [AD_SIZE_970x90, AD_SIZE_728x90, AD_SIZE_970x250]

const RECOMMENDATION_COLUMN_AD_SIZES: googletag.MultiSize = [
  AD_SIZE_300x250,
  AD_SIZE_336x280,
  AD_SIZE_1x1,
]

function getEligibleMultisizeAdSizes(
  sizes: googletag.MultiSize,
  containerWidth: number,
): googletag.MultiSize {
  const hasEligibleDisplaySize = sizes.some(
    (size) =>
      Array.isArray(size) && typeof size[0] === 'number' && size[0] > 1 && size[0] < containerWidth,
  )

  if (!hasEligibleDisplaySize) return []

  return sizes.filter(
    (size) =>
      size === 'fluid' ||
      (Array.isArray(size) &&
        typeof size[0] === 'number' &&
        (size[0] === 1 || size[0] < containerWidth)),
  )
}

const EMPTY_AD_SIZES: googletag.MultiSize = []

const ELIGIBLE_MULTISIZE_MB_AD_SIZES = getEligibleMultisizeAdSizes(MULTISIZE_MB_AD_SIZES, 321)

const NARROW_MULTISIZE_PC_AD_SIZES = getEligibleMultisizeAdSizes(MULTISIZE_PC_AD_SIZES, 729)

const WIDE_MULTISIZE_PC_AD_SIZES = getEligibleMultisizeAdSizes(MULTISIZE_PC_AD_SIZES, 971)

const NARROW_RECOMMENDATION_COLUMN_AD_SIZES = getEligibleMultisizeAdSizes(
  RECOMMENDATION_COLUMN_AD_SIZES,
  301,
)

const WIDE_RECOMMENDATION_COLUMN_AD_SIZES = getEligibleMultisizeAdSizes(
  RECOMMENDATION_COLUMN_AD_SIZES,
  337,
)

/** Returns mobile inventory sizes that fit strictly inside the content column. */
export function getMultisizeMBAdSizes(containerWidth: number): googletag.MultiSize {
  if (containerWidth <= 320) return EMPTY_AD_SIZES
  return ELIGIBLE_MULTISIZE_MB_AD_SIZES
}

/** Returns desktop inventory sizes that fit strictly inside the content column. */
export function getMultisizePCAdSizes(containerWidth: number): googletag.MultiSize {
  if (containerWidth <= 728) return EMPTY_AD_SIZES
  if (containerWidth <= 970) return NARROW_MULTISIZE_PC_AD_SIZES
  return WIDE_MULTISIZE_PC_AD_SIZES
}

/** Returns recommendation-column inventory sizes strictly narrower than its card grid. */
export function getRecommendationColumnAdSizes(containerWidth: number): googletag.MultiSize {
  if (containerWidth <= 300) return EMPTY_AD_SIZES
  if (containerWidth <= 336) return NARROW_RECOMMENDATION_COLUMN_AD_SIZES
  return WIDE_RECOMMENDATION_COLUMN_AD_SIZES
}

export const AD_REFRESH_MIN_INTERVAL_MS = 30_000

export const AD_REFRESH_MAX_INTERVAL_MS = 3_600_000

export const NATIVE_SEARCH_RESULT_AD_SIZES: googletag.MultiSize = ['fluid', AD_SIZE_1x1]
export const STICKY_BANNER_DISMISS_EVENT = 'click_sticky_banner_dismiss'

export const STICKY_BANNER_DISMISS_PAGE_TYPES = {
  home: 'home',
  'content-detail': 'content_detail_page',
  search: 'search_result_page',
  genre: 'genre',
  games: 'games',
} as const satisfies Record<StickyBannerPageType, StickyBannerDismissPageType>
